import { useEffect, useRef, useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  CallState,
  LoadingState,
  UsePSAgentCallProps,
  PSAgentCallReturn,
  Transcript,
  SurveyProgress,
  PS_CONFIG,
  formatTime as commonFormatTime,
} from '@/types/ps-common';
import api from '@/utils/ky';
import {
  Room,
  RoomEvent,
  Track,
  type RemoteTrackPublication,
  type RemoteParticipant,
} from 'livekit-client';

/**
 * LiveKit-based PS Agent Call Hook
 *
 * This hook provides LiveKit voice AI integration for the PS agent route.
 * It uses the same LiveKit infrastructure as useLivekitAgentCall but implements
 * the PSAgentCallReturn interface required by the PS agent components.
 *
 * @param props - PS agent call configuration
 * @returns PSAgentCallReturn interface
 */
export const useLivekitPSAgentCall = ({
  userId,
  version = 2,
  lang = 'en',
  provider = 'openai', // Default to OpenAI
  voice, // Voice ID override for TTS (optional)
  onCallEnded,
}: UsePSAgentCallProps): PSAgentCallReturn => {
  // Core state
  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.INITIAL
  );
  const [error, setError] = useState<string | null>(null);
  const [callState, setCallState] = useState<CallState>(CallState.INCOMING);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Call timer state
  const [callDuration, setCallDuration] = useState<number>(0);
  const startTimeRef = useRef<number>(0);
  const callTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Transcript state
  const [transcript, setTranscript] = useState<Transcript[]>([]);

  // LiveKit-specific state
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const livekitRoomRef = useRef<Room | null>(null);

  // Agent info based on version
  const agentInfo = useMemo(
    () => ({
      profile: {
        name:
          version === 6
            ? PS_CONFIG.nameV6
            : version === 5
              ? PS_CONFIG.nameV5
              : version === 4
              ? PS_CONFIG.nameV4
              : version === 3
                ? PS_CONFIG.nameV3
                : version === 2
                  ? PS_CONFIG.nameChinese
                  : PS_CONFIG.nameEnglish,
        role:
          version === 6
            ? 'AI電話アシスタント'
            : version === 5
              ? 'AI 전화 어시스턴트'
              : version === 4
              ? 'Outbound Call Representative'
              : version === 3
                ? 'Post-Transaction Check Representative'
                : 'Post-Sales Survey Representative',
        avatar:
          version === 6
            ? PS_CONFIG.avatarV6
            : version === 5
              ? PS_CONFIG.avatarV5
              : version === 4
              ? PS_CONFIG.avatar
              : version === 3
                ? PS_CONFIG.avatarV3
                : PS_CONFIG.avatar,
      },
    }),
    [version]
  );

  // Survey progress (not actively tracked in LiveKit mode but required by interface)
  const [currentQuestion, setCurrentQuestion] = useState<string>('');
  const surveyProgress: SurveyProgress = useMemo(
    () => ({
      current: 0,
      total: 0,
    }),
    []
  );

  // Call timer effect
  useEffect(() => {
    if (callState === CallState.ACTIVE) {
      callTimerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
    }

    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [callState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (livekitRoomRef.current) {
        livekitRoomRef.current.disconnect();
      }
    };
  }, []);

  const handleAnswerCall = async () => {
    try {
      setLoadingState(LoadingState.CONNECTING);

      // Request microphone access
      await navigator.mediaDevices.getUserMedia({ audio: true });

      // Create backend session first (required for livekit-agent-call.py to fetch session data)
      // Determine product code based on version and language
      let productCode = 'prudential-ps'; // Default: English post-sales survey
      if (version === 6) {
        productCode = 'nihon-sonpo-japan'; // Japanese outbound call
      } else if (version === 5) {
        productCode = 'human-nonlife-korea'; // Korean outbound call
      } else if (version === 4) {
        productCode = 'prudential-taiwan'; // Taiwan outbound call
      } else if (version === 2 && lang === 'zh') {
        productCode = 'prudential-ps-chinese'; // Chinese post-sales survey
      }

      const sessionResponse = await api
        .post('api/agent/sessions', {
          json: {
            userId,
            productCode,
            mode: 'normal',
            language: lang,
            // Add scriptId to use voice configuration from script_voice_config.py
            scriptId: version === 6
              ? 'nihon-sonpo-japan-outbound'
              : version === 5
                ? 'human-nonlife-korea-outbound'
                : version === 4
                ? 'prudential-taiwan-robocalling'
                : undefined,
            // Add provider to override engine (openai, xai, gemini, etc.)
            provider: version === 6
              ? 'minimax'
              : version === 5
                ? 'cartesia'
                : version === 4
                ? provider
                : undefined,
            // Add voice to override TTS voice (e.g., Cartesia voice UUID)
            voice: version === 6
              ? (voice || 'Japanese_OptimisticYouth')
              : version === 5
                ? (voice || 'cac92886-4b7c-4bc1-a524-e0f79c0381be')
                : (version === 4 && voice ? voice : undefined),
          },
        })
        .json<{ sessionId: string }>();

      const realSessionId = sessionResponse.sessionId;
      setSessionId(realSessionId);
      console.log('Created backend session:', realSessionId);

      // Initialize LiveKit connection
      const room = new Room();
      livekitRoomRef.current = room;

      // Set up event listeners for audio tracks
      room.on(
        RoomEvent.TrackSubscribed,
        (
          track: RemoteTrackPublication['track'],
          _publication: RemoteTrackPublication,
          participant: RemoteParticipant
        ) => {
          if (track?.kind === Track.Kind.Audio) {
            // Attach audio track to play agent's voice
            const audioElement = track.attach();
            document.body.appendChild(audioElement);
            audioElement.play();
            console.log('Agent audio track attached', participant.identity);
          }
        }
      );

      // Handle track unsubscribed
      room.on(
        RoomEvent.TrackUnsubscribed,
        (
          track: RemoteTrackPublication['track'],
          _publication: RemoteTrackPublication,
          participant: RemoteParticipant
        ) => {
          if (track?.kind === Track.Kind.Audio) {
            track.detach().forEach((element) => element.remove());
            console.log('Agent audio track detached', participant.identity);
          }
        }
      );

      // Handle transcription from LiveKit agent
      // Merge consecutive final segments from the same source within a short
      // time window to avoid fragmented chat bubbles (LiveKit STT fires many
      // short final segments per utterance).
      const MERGE_THRESHOLD_MS = 4000;

      room.on(
        RoomEvent.TranscriptionReceived,
        (segments: { id: string; text: string; final: boolean }[], participant?: RemoteParticipant) => {
          console.log('📝 Transcription received:', {
            segmentCount: segments.length,
            participantIdentity: participant?.identity,
            segments: segments.map((s) => ({
              id: s.id,
              text: s.text,
              final: s.final,
            })),
          });

          segments.forEach((segment) => {
            // Only process segments with sufficient length or final segments
            if (segment.text.length > 3 || segment.final) {
              // Only add final transcriptions to avoid word-by-word updates
              if (segment.final) {
                const isAgent = participant?.identity?.startsWith('agent-');
                const source = isAgent ? 'ai' : 'user';
                const now = new Date();

                console.log('📝 Final transcription:', {
                  isAgent,
                  text: segment.text,
                  participantIdentity: participant?.identity,
                });

                setTranscript((prev) => {
                  const lastEntry = prev[prev.length - 1];

                  if (
                    lastEntry &&
                    lastEntry.source === source &&
                    now.getTime() - lastEntry.timestamp.getTime() < MERGE_THRESHOLD_MS
                  ) {
                    const updated = [...prev];
                    updated[updated.length - 1] = {
                      ...lastEntry,
                      message: lastEntry.message + ' ' + segment.text,
                      timestamp: now,
                    };
                    return updated;
                  }

                  return [
                    ...prev,
                    { source, message: segment.text, timestamp: now },
                  ];
                });
              }
            }
          });
        }
      );

      // Handle connection state changes
      room.on(RoomEvent.Connected, () => {
        console.log('Connected to LiveKit room');
      });

      room.on(RoomEvent.Disconnected, () => {
        console.log('Disconnected from LiveKit room');
      });

      room.on(RoomEvent.Reconnecting, () => {
        console.log('Reconnecting to LiveKit room...');
      });

      room.on(RoomEvent.Reconnected, () => {
        console.log('Reconnected to LiveKit room');
      });

      // Handle participant speaking state via attributes (preferred for LiveKit agents)
      room.on(
        RoomEvent.ParticipantAttributesChanged,
        (
          changedAttributes: Record<string, string>,
          participant: RemoteParticipant
        ) => {
          console.log('🔄 Participant attributes changed:', {
            participantIdentity: participant.identity,
            changedAttributes,
          });

          if (participant.identity.startsWith('agent-')) {
            const agentState = changedAttributes['lk.agent.state'];
            const nowSpeaking = agentState === 'speaking';

            console.log('🤖 Agent speaking state change:', {
              participantIdentity: participant.identity,
              agentState,
              nowSpeaking,
            });

            setIsSpeaking(nowSpeaking);
          }
        }
      );

      // Handle participant speaking state via metadata (fallback)
      room.on(
        RoomEvent.ParticipantMetadataChanged,
        (metadata: string | undefined, _participant: RemoteParticipant) => {
          try {
            const meta = metadata ? JSON.parse(metadata) : {};
            if (meta.speaking !== undefined) {
              console.log('🤖 Metadata speaking state:', meta.speaking);
              setIsSpeaking(meta.speaking);
            }
          } catch (err) {
            console.error('Error parsing participant metadata:', err);
          }
        }
      );

      // Get LiveKit token from backend
      // Note: Don't send config parameter when scriptId is present (version 4)
      // to ensure script-based voice configuration is used instead of language config
      const searchParams: Record<string, string> = {
        username: userId || 'anonymous',
        room: realSessionId,
        livekitAgentName:
          import.meta.env.VITE_LIVEKIT_CALL_AGENT_NAME ||
          'LIVEKIT-DEMO-DEV-CALL-AGENT',
      };

      // Only add config for non-scripted versions (not version 4 or 5)
      if (version !== 4 && version !== 5 && version !== 6) {
        searchParams.config = `ps-v${version}-${lang}`;
      }

      const tokenResponse = await api
        .get('livekit/token', {
          searchParams,
        })
        .json<{ token: string }>();

      const livekitUrl = import.meta.env.VITE_LIVEKIT_URL;

      if (!livekitUrl) {
        throw new Error('VITE_LIVEKIT_URL environment variable is not set');
      }

      console.log('Connecting to LiveKit:', {
        url: livekitUrl,
        room: realSessionId,
      });

      // Connect to LiveKit room
      await room.connect(livekitUrl, tokenResponse.token);
      console.log('Successfully connected to LiveKit room');

      // Enable microphone
      await room.localParticipant.setMicrophoneEnabled(true);
      console.log('Microphone enabled');

      // Set call to active state and start timer
      startTimeRef.current = Date.now();
      setLoadingState(LoadingState.ACTIVE);
      setCallState(CallState.ACTIVE);
    } catch (err) {
      console.error('Error starting call:', err);
      setError('Failed to start call session');
      setLoadingState(LoadingState.ERROR);
    }
  };

  const handleEndCall = async () => {
    try {
      setLoadingState(LoadingState.ENDING);

      // Disconnect LiveKit session
      if (livekitRoomRef.current) {
        // Disable microphone before disconnecting
        await livekitRoomRef.current.localParticipant.setMicrophoneEnabled(
          false
        );
        // Disconnect from room
        await livekitRoomRef.current.disconnect();
        console.log('Disconnected from LiveKit room');
        livekitRoomRef.current = null;
      }

      // Log transcript for debugging (no backend session to end)
      if (transcript.length > 0) {
        const formattedTranscript = transcript
          .map(
            (msg) =>
              `${msg.source === 'ai' ? 'Agent' : 'User'}: ${msg.message}`
          )
          .join('\n');
        console.log('Call transcript:', formattedTranscript);
        console.log('Call duration:', (Date.now() - startTimeRef.current) / 1000, 'seconds');
      }

      // End call
      setCallState(CallState.ENDED);

      // Notify parent component
      if (onCallEnded) {
        onCallEnded();
      } else {
        toast.success('Call ended successfully');
      }
    } catch (err) {
      console.error('Error ending call:', err);

      // Still end the call state even if backend fails
      setCallState(CallState.ENDED);

      if (onCallEnded) {
        onCallEnded();
      } else {
        toast.error('Call ended');
      }
    } finally {
      setLoadingState(LoadingState.READY);
    }
  };

  const formatTime = (seconds: number) => {
    return commonFormatTime(seconds);
  };

  return {
    loadingState,
    error,
    callState,
    sessionId,
    conversationId: sessionId, // Use sessionId as conversationId for LiveKit
    agentInfo,
    callDuration,
    transcript,
    isSpeaking,
    currentQuestion,
    surveyProgress,
    handleAnswerCall,
    handleEndCall,
    formatTime,
    retryInitialization: handleAnswerCall,
  };
};
